import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/lib/session";

const FONT_SIZES = ["sm", "md", "lg", "xl"];
const SPACINGS = ["compact", "normal", "wide"];
const THEMES = [
  { key: "calm", label: "Calm" },
  { key: "warm", label: "Warm" },
  { key: "high_contrast", label: "High contrast" },
];
const COLORS = ["#FF7A00", "#2563EB", "#10B981", "#7C3AED", "#111827"];

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setTheme } = useSession();
  const [student, setStudent] = useState(null);

  useEffect(() => { api.getStudent(id).then(setStudent); }, [id]);

  const update = async (patch) => {
    try {
      const updated = await api.updateProfile(id, patch);
      setStudent(s => ({ ...s, profile: updated }));
      if (patch.color_theme) setTheme(patch.color_theme);
      toast.success("Saved!");
    } catch {
      toast.error("Could not save");
    }
  };

  if (!student) return <div className="p-10">Loading…</div>;
  const p = student.profile;

  return (
    <div className="student-view min-h-screen bg-background">
      <header className="px-6 py-4 flex items-center gap-3 border-b border-border">
        <button onClick={() => navigate(`/student/${id}`)} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-from-profile">
          <ArrowLeft className="h-5 w-5" /> Back
        </button>
        <h1 className="text-2xl font-black">My settings</h1>
      </header>

      <main className="max-w-3xl mx-auto p-6 lg:p-10 space-y-8">
        <Section label="Font size" testId="font-size-section">
          <div className="flex gap-3 flex-wrap">
            {FONT_SIZES.map(s => (
              <button
                key={s}
                data-testid={`font-${s}`}
                onClick={() => update({ font_size: s })}
                className={`rounded-2xl px-6 py-4 border min-h-[56px] font-bold ${p.font_size === s ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </Section>

        <Section label="Line spacing" testId="line-spacing-section">
          <div className="flex gap-3 flex-wrap">
            {SPACINGS.map(s => (
              <button
                key={s}
                data-testid={`spacing-${s}`}
                onClick={() => update({ line_spacing: s })}
                className={`rounded-2xl px-6 py-4 border min-h-[56px] font-bold capitalize ${p.line_spacing === s ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </Section>

        <Section label="Color theme" testId="theme-section">
          <div className="flex gap-3 flex-wrap">
            {THEMES.map(t => (
              <button
                key={t.key}
                data-testid={`theme-${t.key}`}
                onClick={() => update({ color_theme: t.key })}
                className={`rounded-2xl px-6 py-4 border min-h-[56px] font-bold ${p.color_theme === t.key ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Section>

        <Section label="Stroke color" testId="stroke-color-section">
          <div className="flex gap-3 flex-wrap">
            {COLORS.map(c => (
              <button
                key={c}
                data-testid={`stroke-${c}`}
                onClick={() => update({ stroke_color: c })}
                aria-label={`Color ${c}`}
                className={`h-14 w-14 rounded-2xl border-4 ${p.stroke_color === c ? "border-foreground" : "border-white"} shadow`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </Section>
      </main>
    </div>
  );
}

function Section({ label, children, testId }) {
  return (
    <section data-testid={testId}>
      <div className="font-black text-xl mb-3">{label}</div>
      {children}
    </section>
  );
}
